import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBlocker } from '../../../hooks/useBlocker/useBlocker';

// Material UI
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

// Icons
import SaveIcon from '@mui/icons-material/Save';

// Comonents
import DetailsTable from './details';
import SaveModal from './saveModal';
import Spinner from '../../../components/spinner';

// Hooks
import useMovement from '../../../hooks/movements/useMovement';
import useNotify from '../../../hooks/notify';
import useAdjustmentTypes from '../../../hooks/adjustmentTypes/useAdjustmentTypes';

import useStyles from './styles';
import Dropdown from '../../../components/inputs/dropdown';
import { statesFilter, types, subTypes } from '../../../models/MovementEntity';
import { Paper } from '@mui/material';
import ObservationModal from './observationModal';
import useUser from '../../../hooks/user/useUser';
import { FabContainer } from '@arredo/reactjs-ui';

const MovementEdition = ({ movementId }) => {
    const { classes } = useStyles();
    const { Notify, variants } = useNotify();
    const user = useUser();
    const navigate = useNavigate();
    const [openSave, setOpenSave] = useState();
    const [openObservation, setOpenObservation] = useState(false);
    const [saveLoading, setSaveLoading] = useState();
    const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);
    const {
        movement, loading,
        addProduct, removeProduct,
        changeProduct, editProduct,
        edit, save, addProductMassive, editAjustmentTypeMassive
    } = useMovement(movementId !== 'ingress' ? movementId : null);
    const { adjustmentTypes } = useAdjustmentTypes({ types: movement.type, subType: movement.subType });

    useEffect(() => {
        window.onbeforeunload = shouldBlockNavigation
            ? () => true
            : undefined;

        return () => {
            window.onbeforeunload = undefined;
        }
    }, [shouldBlockNavigation])

    useEffect(() => {
        if (movement.subType === subTypes.Adjustment) {
            const title = movement.type === types.Ingress ? "Auditoria-Ingreso" : "Auditoria-Egreso";
            edit({ ...movement, reason: title, state: "Accepted" });
        }
        edit({ ...movement, createdBy: user.email });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldBlockNavigation])

    useBlocker((tx) => {
        if (shouldBlockNavigation && !window.confirm("Si te vas perderas todo el progreso realizado hasta ahora. ¿Estas seguro?")) {
            return;
        }
        tx.retry();
    }, shouldBlockNavigation);

    const handlerAddProduct = async (refId, adjustment) => {
        if (!(await addProduct(refId, adjustment)))
            Notify("No se encontró el producto que quiere agregar.", variants.INFO);
    }

    const handlerAddProductsMassive = async (refIds) => {
        if (!(await addProductMassive(refIds)))
            Notify("Error al intentar agregar productos.", variants.INFO);
    }

    const handlerRemoveProduct = (productId) => {
        if (!removeProduct(productId))
            Notify("No se encontro el producto que quiere eliminar.", variants.INFO);
    }

    const handleSaveEditProduct = (refId, payload) => {
        const method = refId === payload.refId ? editProduct : changeProduct
        method(refId, payload);
    }
    const handleOpenSaveModal = () => {
        if (!movement.details || movement.details.length <= 0)
            return Notify("El movimiento no tiene ningun detalle ingresado.", variants.INFO);
        if (movement.subType === subTypes.Adjustment) {
            const aux = movement.details.filter(a => !a.adjustmentTypes?.description);
            if (aux?.length > 0)
                return Notify("Uno o mas productos no tiene asignado un tipo de ajuste.", variants.INFO);
            else
                handleSaveMovement()
        } else
            setOpenSave(true);
    }

    const isInvalid = () =>
        !movement.details ||
        movement.details.length <= 0 ||
        movement.details.some(i => i.error) ||
        (movement.subType === subTypes.Adjustment && movement.details.some(i => !i.adjustmentTypes?.description));

    const handleSaveMovement = () => {
        setOpenObservation(true);
    }

    const handleSaveObservation = async () => {
        setOpenObservation(false);
        if (!movement.warehouseId && !movement.warehouse?.id)
            return Notify("El usuario no tiene una sucursal asociada.", variants.ERROR);

        setSaveLoading(true);
        setShouldBlockNavigation(false);

        save()
            .then(() => {
                Notify(`Movimiento ${movementId ? 'actualizado' : 'creado'} exitosamente.`, variants.SUCCESS);
                navigate('/movements');
            })
            .catch(err => {
                if (err) console.error(err);
                Notify("Ocurrio un error al intentar guardar el movimiento.", variants.ERROR);
            })
            .finally(() => {
                setShouldBlockNavigation(true);
                setSaveLoading(false);
            });
    }

    const handleChangeState = (event) => {
        edit({ ...movement, state: event.target.value });
    }

    const handleAdjustmentTypes = async (value) => {
        editAjustmentTypeMassive(value);
    }

    return (
        <>
            {
                (loading || saveLoading) &&
                <Spinner lockScreen />
            }
            {
                openSave &&
                <SaveModal
                    open={openSave}
                    movement={movement}
                    onChange={edit}
                    onClose={() => setOpenSave(false)}
                    onSave={handleSaveMovement}
                />
            }
            {
                openObservation &&
                <ObservationModal
                    open={openObservation}
                    movement={movement}
                    onChange={edit}
                    onClose={() => setOpenObservation(false)}
                    onSave={handleSaveObservation}
                />
            }
            <Grid container direction='column' spacing={2}>
                {!movementId &&
                    <Grid>
                        <Typography variant='h4' component='h2'>
                            {`Nuevo movimiento de ${movement.subType === subTypes.Adjustment ? "ajuste" : "recepción"}`}
                        </Typography>
                    </Grid>}
                <Grid>
                    {
                        !!movement.id &&
                        <Paper>
                            <Grid container spacing={1} alignItems='center' style={{ padding: '8px 16px' }}>
                                <Typography color='primary' variant='h5'>
                                    Estado
                                </Typography>
                                <Dropdown
                                    size='small'
                                    onChange={handleChangeState}
                                    values={statesFilter}
                                    value={movement.state}
                                    style={{ marginLeft: '10px' }}
                                />
                            </Grid>
                        </Paper>
                    }
                    <DetailsTable
                        details={movement.details}
                        onRemove={handlerRemoveProduct}
                        onAdd={handlerAddProduct}
                        onAddMassive={handlerAddProductsMassive}
                        onChange={handleSaveEditProduct}
                        adjustmentTypesChange={handleAdjustmentTypes}
                        movementType={movement.type || types.Ingress}
                        adjustmentTypesOptions={adjustmentTypes}
                    />
                </Grid>
            </Grid>
            <FabContainer>
                <Fab
                    color='primary'
                    variant="extended"
                    className={classes.fab}
                    onClick={handleOpenSaveModal}
                    disabled={isInvalid()}
                >
                    <SaveIcon className={classes.extendedIcon} />
                    Guardar
                </Fab>
            </FabContainer>
        </>
    );
}

export default MovementEdition;